// Serializes writes per dashboard: overlapping writes (theme pick + drag-end, double
// undo, a rename racing an in-flight save) would otherwise read the same cached
// concurrency token — or bump it mid-flight — and 409 against the user's own edit.
// Each link runs only after the previous write has landed and refreshed the token.
const writeChainByDashboardId = new Map<string, Promise<unknown>>();

export const enqueueDashboardWrite = <T>(
  webDashboardId: string,
  write: () => Promise<T>
): Promise<T> => {
  const previous = writeChainByDashboardId.get(webDashboardId) ?? Promise.resolve();

  const thisWrite = previous
    // A failed predecessor must not block the chain (its caller already handled the error).
    .catch(() => undefined)
    .then(write);

  writeChainByDashboardId.set(webDashboardId, thisWrite);
  // Evict once settled (unless a newer write already replaced this entry) so the map
  // doesn't grow by one settled promise per dashboard visited for the page lifetime.
  thisWrite
    .catch(() => undefined)
    .finally(() => {
      if (writeChainByDashboardId.get(webDashboardId) === thisWrite) {
        writeChainByDashboardId.delete(webDashboardId);
      }
    });
  return thisWrite;
};

// Bumped when a chained write lands row content that payloads captured earlier can't
// contain — a concurrent-change merge (the server rebased over another writer), or a
// rename PATCH (new title). A save link compares this against the value it captured at
// enqueue time: a change means its full-config payload predates the row, so it must pair
// that payload with its enqueue-time token — forcing the server's delta rebase (or a
// visible 409) — instead of the fresh cache token, which would token-match and silently
// full-write over the newer content.
const writeEpochByDashboardId = new Map<string, number>();

export const getDashboardWriteEpoch = (webDashboardId: string): number =>
  writeEpochByDashboardId.get(webDashboardId) ?? 0;

export const bumpDashboardWriteEpoch = (webDashboardId: string): void => {
  writeEpochByDashboardId.set(webDashboardId, getDashboardWriteEpoch(webDashboardId) + 1);
};
